import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {CompanyModel} from '../../../../../../libs/shared/core/src/models/api/company/companyModel';
import {RecipientRolesEnum} from '../../../../../../libs/shared/core/src/models/api/recipientModel';
import {globalNamespace} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {fetchApiAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {buildVisualRecipientFromMatch} from '../../../../../helpers/models/recipientHelpers';
import {
  ApiRecipientContactMatches,
  RecipientMatchTypesEnum,
} from '../../../../../models/api/recipientMatchModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {buildRecipientContactMatchSearchUrl} from '../../../../../services/recipientMatches';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {getFindTeammateAndOnlineStatusForVisualContact} from '../../../../../store/data/contacts/contactsSelectors';
import {AutocompleteDropdown} from '../../../../common/dropdowns/autocomplete/autocompleteDropdown';
import {RecipientDropdownItem} from '../../../../common/dropdowns/autocomplete/recipientDropdownItem';
import {AsyncDropdownSource, DropdownFetcher} from '../../../../common/dropdowns/source/asyncDropdownSource';
import {InboxesSearchQueryBuilderTextInput} from './inboxesSearchQueryBuilderTextInput';
import {
  InboxesSearchQueryBuilderInputValue,
  InboxesSearchQueryBuilderRecipientInputConfig,
} from './inboxesSearchQueryBuilderTypes';

/*
 * Props.
 */

interface InboxesSearchQueryBuilderRecipientInputProps
  extends InboxesSearchQueryBuilderRecipientInputConfig,
    InboxesSearchQueryBuilderInputValue {}

/*
 * Component.
 */

export const InboxesSearchQueryBuilderRecipientInput: FC<InboxesSearchQueryBuilderRecipientInputProps> = (
  props,
) => {
  const {inputValue, onChange, namespace, ...rest} = props;
  const {value} = inputValue;

  const role = useMemo(() => mapModifierToRole(rest.modifier), [rest.modifier]);

  const inputRef = useRef<HTMLDivElement>(null);

  /*
   * State.
   */

  const [query, setQuery] = useState<string>(value);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const onQueryChange = (newQuery: string) => {
    setIsDropdownOpen(newQuery.length > 0);
    setQuery(newQuery);

    // Make sure to update the query builder state.
    onChange({
      ...inputValue,
      value: newQuery,
    });
  };

  // If `value` changed (for example when clicking on `reset`), update `query`.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  /*
   * Recipient fetcher.
   */

  const company = useAppSelector(getCompany);

  const findTeammateAndOnlineStatusForVisualContact = useAppSelector(
    getFindTeammateAndOnlineStatusForVisualContact,
  );
  const enrichVisualRecipient = useCallback(
    (vr: VisualRecipientModel) => {
      const contactWithTeammateAndOnlineStatus = findTeammateAndOnlineStatusForVisualContact(vr);
      return {
        ...vr,
        ...contactWithTeammateAndOnlineStatus,
      };
    },
    [findTeammateAndOnlineStatusForVisualContact],
  );

  const {fetcher} = useQueryBuilderRecipientsFetcher({
    company,
    namespace,
    role,
    enrichVisualRecipient,
  });

  /*
   * Row renderer.
   */

  const onRecipientSelect = useCallback(
    (recipient: VisualRecipientModel) => {
      const {handle} = recipient.contact;

      setQuery(handle);
      onChange({
        ...inputValue,
        value: handle,
      });

      // Close the dropdown.
      setIsDropdownOpen(false);
    },
    [onChange, inputValue],
  );

  const renderRow = useCallback(
    (recipient: VisualRecipientModel) => (
      <RecipientDropdownItem
        key={recipient.contact.uid}
        recipient={recipient}
        onSelect={() => onRecipientSelect(recipient)}
      />
    ),
    [onRecipientSelect],
  );

  /*
   * Dropdown.
   */

  const source = useMemo(
    () =>
      new AsyncDropdownSource({
        fetcher,
        renderRow,
      }),
    [fetcher, renderRow],
  );

  const onDropdownRequestClose = () => {
    setIsDropdownOpen(false);
  };

  return (
    <>
      <div ref={inputRef}>
        <InboxesSearchQueryBuilderTextInput
          {...rest}
          inputValue={{value: query}}
          onChange={(newValue) => onQueryChange(newValue.value)}
        />
      </div>
      {isDropdownOpen && (
        <AutocompleteDropdown
          anchor={inputRef}
          onRequestClose={onDropdownRequestClose}
          query={query}
          source={source}
          maxWidth={300}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function mapModifierToRole(modifier: ModifiersEnum) {
  if (modifier === ModifiersEnum.TO) {
    return RecipientRolesEnum.TO;
  }

  // Fallback to FROM.
  return RecipientRolesEnum.FROM;
}

interface QueryBuilderRecipientsFetcherProps {
  company: CompanyModel;
  namespace?: string;
  role: RecipientRolesEnum;
  enrichVisualRecipient?: (recipient: VisualRecipientModel) => VisualRecipientModel;
}

function useQueryBuilderRecipientsFetcher(props: QueryBuilderRecipientsFetcherProps) {
  const {company, namespace = globalNamespace, role, enrichVisualRecipient} = props;

  const fetcher: DropdownFetcher<VisualRecipientModel> = useCallback(
    async (query, _items, cancelToken) => {
      // If the query is empty, just update the state.
      if (!query) {
        return {items: [], canLoadMore: false};
      }

      const url = buildRecipientContactMatchSearchUrl(company, query);
      const matches = await fetchApiAsync(ApiRecipientContactMatches, url, {cancelToken});

      // Map the recipient matches to Visual Recipients (easier to deal with).
      const recipients = matches
        .filter((m) => m.type === RecipientMatchTypesEnum.CONTACT)
        .map((m) => buildVisualRecipientFromMatch(namespace, role, m))
        .map((vr) => (enrichVisualRecipient ? enrichVisualRecipient(vr) : vr));

      return {
        items: recipients,
        canLoadMore: false,
      };
    },
    [company, enrichVisualRecipient, namespace, role],
  );

  return {fetcher};
}
